import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    combineLatest,
    concatMap,
    filter,
    first,
    map,
    Observable,
    of,
    switchMap,
    throwError,
} from 'rxjs';
import { NOT_FOUND_LABEL } from 'src/app/config/app.config';
import { EVENT_BUFFER_PROTOCOL } from 'src/app/core/entities/event-buffer/enums/event-protocol.enum';
import { OrganizationRepositoryAdapter } from 'src/app/core/entities/organization/infrastructure/adapters/organization-repository.adapter';
import { SESSION_STORAGE_REPOSITORY } from 'src/app/core/entities/session/infrastructure/interfaces/session-storage.repository';
import { SOCKET_AUTHOR_MODEL } from 'src/app/core/enums/events-buffer/socket/socket-author-model.enum';
import { SOCKET_TRACKING_EVENT } from 'src/app/core/enums/events-buffer/socket/socket-tracking-event.enum';
import { Logger } from 'src/app/core/interfaces/Logger';
import { IOrganization } from 'src/app/core/entities/organization/domain/interfaces/organization.interface';
import { ISocketPayload } from 'src/app/core/interfaces/socket-payload.interface';
import { AppContextService } from 'src/app/shared/services/app-context/app-context.service';
import { generateIndexDBKey } from 'src/app/shared/utils/indexdb.utils';
import { SocketService } from '../../socket/socket.service';
import { TRACKER_TYPE } from '../enums/tracker.enum';
import { ITrackerPaymentBody } from '../interfaces/payment-tracker.interface';
import { TrackerImpl } from '../interfaces/tracker.model';
import { ITrackingRecord } from '../interfaces/tracking-record.interface';
import { TrackingService } from '../tracking.service';
import { AccountStorePort } from 'src/app/core/entities/account/domain/ports/account-store.port';
import { ACCOUNT_STORE } from 'src/app/core/entities/account/domain/tokens/account-store.token';

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';

/**
 * Service responsible for tracking payment-related events in the application.
 * Handles adding, removing, and sending tracking data for payments.
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentTrackerService {
  private readonly logger = inject(Logger);
  private readonly socketService = inject(SocketService);
  private readonly appContextService = inject(AppContextService);
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly trackingService = inject(TrackingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sessionStorageRepository = inject(SESSION_STORAGE_REPOSITORY);
  private readonly accountStoreService = inject<AccountStorePort>(ACCOUNT_STORE);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  private readonly tracker: TrackerImpl<ITrackerPaymentBody[]> =
    new TrackerImpl<ITrackerPaymentBody[]>();

  /**
   * Initializes the service by setting up event listeners for adding,
   * removing, and sending tracking data.
   */
  constructor() {
    this.initAddListener();
    this.initRemoveListener();
    this.initSendListener();
  }

  /**
   * Sets up a listener for add track events specific to payment tracking.
   * When a payment tracking record is received, it adds the tracking data.
   */
  private initAddListener(): void {
    this.trackingService.onAddTrack$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (record): record is ITrackingRecord<ITrackerPaymentBody> =>
            record.type === TRACKER_TYPE.payment
        )
      )
      .subscribe((record) => {
        if (record.body) {
          this.add(record.body);
        }
      });
  }

  /**
   * Sets up a listener for send track events specific to payment tracking.
   * When a send event is received, it sends the collected tracking data.
   */
  private initSendListener(): void {
    this.trackingService.onSendTrack$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (record): record is ITrackingRecord<ITrackerPaymentBody> =>
            record.type === TRACKER_TYPE.payment
        )
      )
      .subscribe((record) => {
        this.send(record.trackId);
      });
  }

  /**
   * Sets up a listener for remove track events specific to payment tracking.
   * When a remove event is received, it removes the current payment's tracking data.
   */
  private initRemoveListener(): void {
    this.trackingService.onRemoveTrack$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (record): record is ITrackingRecord<ITrackerPaymentBody> =>
            record.type === TRACKER_TYPE.payment
        )
      )
      .subscribe((record) => {
        this.remove(record.trackId);
      });
  }

  /**
   * Adds tracking data for the current payment.
   * Retrieves the current account, gets any existing track data for the payment,
   * adds the new data with a timestamp, and updates the tracker.
   *
   * @param body - The payment tracking data to add
   */
  private add(body: ITrackerPaymentBody): void {
    this.sessionStorageRepository
      .get()
      .pipe(
        first(),
        concatMap((session) => {
          if (!session) {
            return throwError(() =>
              this.logger.error(
                new Error(
                  `[PaymentTrackerService][add] current session not found`
                )
              )
            );
          }

          const currentAccount = this.accountStoreService.getSelectedAccount();

          if (!currentAccount?.trackId) {
            this.logger.error(
              new Error(
                `[PaymentTrackerService][add] currentAccount.trackId is empty`
              )
            );

            return throwError(
              () => new Error('Current account trackId is empty')
            );
          }

          // Use a payment-specific track ID to separate payment logs from account logs
          const paymentTrackId = `payment-${currentAccount.trackId}`;

          return this.tracker
            .getTrack(paymentTrackId)
            .pipe(map((track) => ({ paymentTrackId, track })));
        }),
        concatMap(({ paymentTrackId, track }) => {
          const bodyAux = track?.body ? [...track.body] : [];

          bodyAux.push({
            ...body,
            timestamp: Date.now(),
          });

          return this.tracker.addTrack(
            paymentTrackId,
            TRACKER_TYPE.payment,
            bodyAux
          );
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /**
   * Removes tracking data for the current payment.
   * Gets the current account and removes its associated payment tracking data.
   *
   * @param specificTrackId - Optional specific trackId to use instead of current account's trackId
   */
  private remove(specificTrackId?: string): void {
    this.sessionStorageRepository
      .get()
      .pipe(
        first(),
        switchMap((session) => {
          if (!session) {
            return throwError(() =>
              this.logger.error(
                new Error(
                  `[PaymentTrackerService][remove] current session not found`
                )
              )
            );
          }

          let paymentTrackId: string;

          if (specificTrackId) {
            paymentTrackId = `payment-${specificTrackId}`;
          } else {
            const currentAccount = this.accountStoreService.getSelectedAccount();

            paymentTrackId = `payment-${currentAccount?.trackId ?? ''}`;
          }

          return this.tracker.removeTrack(paymentTrackId);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Sends the collected tracking data for the current payment through the socket service.
   * Prepares the payload with account information, organization data, waiter details,
   * and the payment tracking data before emitting the event.
   *
   * @param specificTrackId - Optional specific trackId to use instead of current account's trackId
   */
  private send(specificTrackId?: string): void {
    this.getPayloadData()
      .pipe(
        first(),
        switchMap((payloadData) => {
          let paymentTrackId: string;

          if (specificTrackId) {
            paymentTrackId = `payment-${specificTrackId}`;
          } else {
            const currentAccount = this.accountStoreService.getSelectedAccount();

            paymentTrackId = `payment-${currentAccount?.trackId ?? ''}`;
          }

          return combineLatest({
            payloadData: of(payloadData),
            track: this.tracker.getTrack(paymentTrackId),
          });
        }),
        switchMap((track) => {
          const worker = this.serviceStateService.getCurrentWorker();
          const currentAccount = this.accountStoreService.getSelectedAccount();

          const payload: ISocketPayload = {
            negocio_id: currentAccount?.negocio_id ?? NOT_FOUND_LABEL,
            action: SOCKET_TRACKING_EVENT.create,
            customData: TRACKER_TYPE.payment,
            author: {
              authorId: worker?.id ?? NOT_FOUND_LABEL,
              model: SOCKET_AUTHOR_MODEL.waiter,
            },
            device: this.appContextService.deviceName,
            organization: this.appContextService.organizationName,
            serviceId: this.appContextService.serviceId,
            timestamp: Date.now(),
            payload: {
              track,
            },
          };

          return this.socketService.prepareToEmitEvent({
            _id: generateIndexDBKey(),
            name: SOCKET_TRACKING_EVENT.create,
            payload,
            protocol: EVENT_BUFFER_PROTOCOL.socket,
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Gathers data needed for the tracking payload.
   * Retrieves the current organization and account information.
   *
   * @returns An Observable with the organization data
   */
  private getPayloadData(): Observable<IOrganization | null> {
    return this.organizationService.getCurrentOrganization().pipe(first());
  }
}
